import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ImageCroppedEvent, ImageCropperComponent, ImageTransform, LoadedImage } from 'ngx-image-cropper';
import { AvatarService, AvatarUploadResult } from '../../services/avatar.service';
import { ToastService } from '../../services/toast.service';
import { ButtonComponent } from '../button/button.component';

/** Max avatar upload size (5 MB). */
const MAX_AVATAR_SIZE = 5 * 1024 * 1024;

export interface AvatarCropDialogResult {
  photoID: number;
  url?: string;
}

/**
 * Round 1:1 avatar crop editor for the jobs app. Mirrors the sameta-fe HR
 * cropper UX (round cropper, 256px PNG, zoom + rotate) but rebuilt standalone +
 * Transloco and wired to the candidate-facing AvatarService.
 */
@Component({
  selector: 'app-avatar-crop-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, TranslocoModule, ImageCropperComponent, ButtonComponent],
  templateUrl: './avatar-crop-dialog.component.html',
  styleUrls: ['./avatar-crop-dialog.component.scss'],
})
export class AvatarCropDialogComponent {
  /** File fed to the cropper (from the picker or drag-and-drop). */
  imageFile: File | undefined;
  /** base64 of the current crop selection. */
  croppedImage = '';
  /** Set once a file has loaded into the cropper. */
  imageData: LoadedImage | null = null;
  originalFile: File | null = null;

  private scale = 1;
  private rotate = 0;
  transform: ImageTransform = { scale: 1 };

  loading = false;

  constructor(
    public dialogRef: MatDialogRef<AvatarCropDialogComponent, AvatarCropDialogResult>,
    private readonly avatarService: AvatarService,
    private readonly toastService: ToastService,
    private readonly transloco: TranslocoService,
  ) {}

  fileChangeEvent(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectFile(input.files?.[0] ?? null);
    // Reset so picking the same file again still fires (change) next time.
    input.value = '';
  }

  /** Validate and load a chosen image into the cropper. */
  private selectFile(file: File | null): void {
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      this.toastService.error(
        this.transloco.translate('pages.profile.avatar.dialog.errorTitle'),
        this.transloco.translate('pages.profile.avatar.dialog.loadFailed'),
      );
      return;
    }
    // Reset transforms when a new image is chosen.
    this.scale = 1;
    this.rotate = 0;
    this.transform = { scale: 1 };
    this.croppedImage = '';
    this.originalFile = file;
    this.imageFile = file;
  }

  async imageCropped(e: ImageCroppedEvent): Promise<void> {
    if (e.base64) {
      this.croppedImage = e.base64;
    } else if (e.blob) {
      this.croppedImage = await this.blobToBase64(e.blob);
    }
  }

  imageLoaded(image: LoadedImage): void {
    this.imageData = image;
  }

  imageLoadFailed(): void {
    this.toastService.error(
      this.transloco.translate('pages.profile.avatar.dialog.errorTitle'),
      this.transloco.translate('pages.profile.avatar.dialog.loadFailed'),
    );
  }

  rotateCcw(): void {
    this.rotate -= 90;
    this.transform = { ...this.transform, rotate: this.rotate };
  }

  rotateCw(): void {
    this.rotate += 90;
    this.transform = { ...this.transform, rotate: this.rotate };
  }

  zoomIn(): void {
    this.scale += 1;
    this.transform = { ...this.transform, scale: this.scale };
  }

  zoomOut(): void {
    if (this.scale > 1) {
      this.scale -= 1;
      this.transform = { ...this.transform, scale: this.scale };
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    if (!this.croppedImage || !this.originalFile) {
      return;
    }

    const blob = this.base64ToBlob(this.croppedImage);
    if (!blob) {
      return;
    }

    if (blob.size > MAX_AVATAR_SIZE) {
      const maxMb = (MAX_AVATAR_SIZE / (1024 * 1024)).toFixed(0);
      this.toastService.error(
        this.transloco.translate('pages.profile.avatar.dialog.errorTitle'),
        this.transloco.translate('pages.profile.avatar.dialog.tooLarge', { size: maxMb }),
      );
      return;
    }

    const fileName = this.replaceExtensionWithJpg(this.originalFile.name);
    this.loading = true;
    this.avatarService.uploadAvatar(blob, blob.type, fileName).subscribe({
      next: (result: AvatarUploadResult) => {
        this.loading = false;
        this.toastService.success(
          this.transloco.translate('pages.profile.avatar.dialog.successTitle'),
          this.transloco.translate('pages.profile.avatar.dialog.successMessage'),
        );
        this.dialogRef.close(result);
      },
      error: (error) => {
        this.loading = false;
        const message = error?.error?.message || error?.message || this.transloco.translate('pages.profile.avatar.dialog.uploadFailed');
        this.toastService.error(this.transloco.translate('pages.profile.avatar.dialog.errorTitle'), message);
      },
    });
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(blob);
    });
  }

  /** Convert a `data:...;base64,...` string to a typed Blob. */
  private base64ToBlob(dataUrl: string): Blob | null {
    const parts = dataUrl.split(',');
    if (parts.length < 2) {
      return null;
    }
    const mimeType = parts[0].replace('data:', '').replace(';base64', '') || 'image/png';
    const byteCharacters = atob(parts[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  }

  private replaceExtensionWithJpg(name: string): string {
    const base = name.includes('.') ? name.slice(0, name.lastIndexOf('.')) : name;
    return `${base || 'avatar'}.jpg`;
  }
}
